import { trpc } from "@/lib/trpc";
import { type UploadProgress, uploadToS3 } from "@/lib/upload-to-s3";
import { cn } from "@/lib/utils";
import { PAYROLL_SYSTEMS } from "@payrollmart-invoice/shared";
import { Upload } from "lucide-react";
import { useCallback, useState } from "react";
import { type FileRejection, useDropzone } from "react-dropzone";
import { toast } from "sonner";

const ACCEPTED_TYPES = {
	"application/pdf": [".pdf"],
	"application/vnd.ms-excel": [".xls"],
	"application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
};

const MAX_SIZE = 10 * 1024 * 1024;

type PayrollSystem = (typeof PAYROLL_SYSTEMS)[number];

type UploadItem = {
	file: File;
	status: "pending" | "uploading" | "confirming" | "extracting" | "done" | "error";
	progress: UploadProgress | null;
	error: string | null;
	documentId: string | null;
};

export function UploadDropzone({ onUploadComplete }: { onUploadComplete?: () => void }) {
	const [uploads, setUploads] = useState<UploadItem[]>([]);
	const [payrollSystem, setPayrollSystem] = useState<PayrollSystem | "">("");

	const requestUpload = trpc.documents.requestUpload.useMutation();
	const confirmUpload = trpc.documents.confirmUpload.useMutation();
	const startExtraction = trpc.extractions.startExtraction.useMutation();

	const processFile = useCallback(
		async (file: File, system: PayrollSystem | undefined) => {
			const updateItem = (patch: Partial<UploadItem>) => {
				setUploads((prev) => prev.map((u) => (u.file === file ? { ...u, ...patch } : u)));
			};

			setUploads((prev) => [
				...prev,
				{ file, status: "pending", progress: null, error: null, documentId: null },
			]);

			try {
				updateItem({ status: "uploading" });
				const { uploadUrl, documentId } = await requestUpload.mutateAsync({
					fileName: file.name,
					contentType: file.type as keyof typeof ACCEPTED_TYPES,
					fileSize: file.size,
					payrollSystem: system,
				});

				updateItem({ documentId });

				await uploadToS3(uploadUrl, file, (progress) => {
					updateItem({ progress });
				});

				updateItem({ status: "confirming" });
				await confirmUpload.mutateAsync({ documentId });

				updateItem({ status: "extracting" });
				try {
					await startExtraction.mutateAsync({ documentId });
				} catch (extractionError) {
					const message =
						extractionError instanceof Error ? extractionError.message : "Extraction failed";
					updateItem({ status: "error", error: message });
					toast.error(`Extraction failed for ${file.name}. You can retry from the document page.`);
					// File is uploaded — let the parent refresh so the user can open the
					// doc page to retry — but don't also fire the "uploaded successfully"
					// toast. The earlier code fell through to both toasts.
					onUploadComplete?.();
					return;
				}

				updateItem({ status: "done" });
				toast.success(`${file.name} uploaded successfully`);
				onUploadComplete?.();
			} catch (error) {
				const message = error instanceof Error ? error.message : "Upload failed";
				updateItem({ status: "error", error: message });
				toast.error(`Failed to upload ${file.name}: ${message}`);
			}
		},
		[requestUpload, confirmUpload, startExtraction, onUploadComplete],
	);

	const onDrop = useCallback(
		(accepted: File[], rejected: FileRejection[]) => {
			for (const r of rejected) {
				const reason = r.errors.map((e) => e.message).join(", ");
				toast.error(`${r.file.name}: ${reason}`);
			}
			const system = payrollSystem || undefined;
			for (const file of accepted) {
				processFile(file, system);
			}
		},
		[processFile, payrollSystem],
	);

	const { getRootProps, getInputProps, isDragActive } = useDropzone({
		onDrop,
		accept: ACCEPTED_TYPES,
		maxSize: MAX_SIZE,
		multiple: true,
	});

	const activeUploads = uploads.filter((u) => u.status !== "done");

	return (
		<div className="space-y-3">
			{/* Payroll system selector */}
			<div className="flex items-center gap-2">
				<label htmlFor="payrollSystem" className="text-xs font-medium text-[#1e1e20]">
					Payroll System
				</label>
				<select
					id="payrollSystem"
					value={payrollSystem}
					onChange={(e) => setPayrollSystem(e.target.value as PayrollSystem | "")}
					className="h-8 rounded border border-[#d1d5db] bg-white px-2 text-sm text-[#1e1e20] focus:border-[#4f46e5] focus:outline-none focus:ring-2 focus:ring-[#4f46e5]/20"
				>
					<option value="">Auto-detect</option>
					{PAYROLL_SYSTEMS.map((sys) => (
						<option key={sys} value={sys}>
							{sys}
						</option>
					))}
				</select>
				<span className="text-xs text-[#9ca3af]">
					Auto-detected from file content, or select manually
				</span>
			</div>

			<div
				{...getRootProps()}
				className={cn(
					"flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed px-6 py-10 transition-colors duration-150",
					isDragActive
						? "border-[#16a34a] bg-[#f0fdf4]"
						: "border-[#d1d5db] bg-white hover:border-[#6b7280]",
				)}
			>
				<input {...getInputProps()} />
				<Upload
					className={cn("h-8 w-8", isDragActive ? "text-[#16a34a]" : "text-[#9ca3af]")}
					strokeWidth={1.5}
				/>
				{isDragActive ? (
					<p className="mt-3 text-sm font-medium text-[#16a34a]">Drop files here</p>
				) : (
					<div className="mt-3 text-center">
						<p className="text-sm text-[#1e1e20]">
							<span className="font-semibold text-[#4f46e5]">Click to upload</span> or drag and drop
						</p>
						<p className="mt-1 text-xs text-[#9ca3af]">PDF, XLS, XLSX up to 10MB</p>
					</div>
				)}
			</div>

			{activeUploads.length > 0 && (
				<div className="space-y-2">
					{activeUploads.map((item) => (
						<div
							key={item.documentId ?? item.file.name}
							className="rounded border border-[#e5e7eb] bg-white px-4 py-3"
						>
							<div className="flex items-center justify-between text-sm">
								<span className="truncate font-medium text-[#1e1e20]">{item.file.name}</span>
								<span className="ml-2 text-xs text-[#6b7280]">
									{item.status === "uploading" && item.progress
										? `${item.progress.percentage}%`
										: item.status}
								</span>
							</div>
							{item.status === "uploading" && item.progress && (
								<div className="mt-2 h-1 w-full rounded-full bg-[#f3f4f6]">
									<div
										className="h-full rounded-full bg-[#16a34a] transition-all duration-200"
										style={{ width: `${item.progress.percentage}%` }}
									/>
								</div>
							)}
							{item.error && <p className="mt-1 text-xs text-[#dc2626]">{item.error}</p>}
						</div>
					))}
				</div>
			)}
		</div>
	);
}
